import { useMemo } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { supabase } from "@/services/supabase";
import { useSessionStore } from "./sessionStore";
import { useCompanyUserStore } from "./companyUserStore";

export type ModuleKey = "budgets" | "financial_modeling";

export interface CompanyModule {
  module_key: ModuleKey;
  module_name: string;
  status: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  cancel_at_period_end: boolean | null;
  entitled: boolean;
}

interface EntitlementsStore {
  modules: CompanyModule[];
  isEntitlementsLoading: boolean;
  entitlementsError: string | null;

  fetchEntitlements: () => Promise<void>;
  clearEntitlementsData: () => void;

  hasModule: (moduleKey: ModuleKey) => boolean;
  hasAnyModule: () => boolean;
  getEntitledModules: () => CompanyModule[];
}

export const useEntitlementsStore = create<EntitlementsStore>()(
  devtools(
    (set, get) => ({
      modules: [],
      isEntitlementsLoading: false,
      entitlementsError: null,

      fetchEntitlements: async () => {
        const session = useSessionStore.getState().session;
        const companyUser = useCompanyUserStore.getState().companyUser;

        if (!session?.user || !companyUser) {
          set(
            { entitlementsError: "No session or company user" },
            false,
            "fetchEntitlements/noAuth",
          );
          return;
        }

        if (get().isEntitlementsLoading) return;

        set(
          { isEntitlementsLoading: true, entitlementsError: null },
          false,
          "fetchEntitlements/start",
        );

        try {
          const { data, error } = await supabase.rpc("get_company_modules");
          if (error) throw error;

          const modules: CompanyModule[] = (data ?? []).map((row: any) => ({
            module_key: row.module_key,
            module_name: row.module_name,
            status: row.status,
            current_period_end: row.current_period_end,
            trial_ends_at: row.trial_ends_at,
            cancel_at_period_end: row.cancel_at_period_end,
            entitled: Boolean(row.entitled),
          }));

          set(
            {
              modules,
              isEntitlementsLoading: false,
              entitlementsError: null,
            },
            false,
            "fetchEntitlements/success",
          );
        } catch (error) {
          const errorMessage = error instanceof Error
            ? error.message
            : "Unknown error";
          set(
            {
              modules: [],
              isEntitlementsLoading: false,
              entitlementsError: errorMessage,
            },
            false,
            "fetchEntitlements/error",
          );
          console.error("Error fetching entitlements:", error);
        }
      },

      clearEntitlementsData: () => {
        set(
          {
            modules: [],
            isEntitlementsLoading: false,
            entitlementsError: null,
          },
          false,
          "clearEntitlementsData",
        );
      },

      hasModule: (moduleKey) => {
        const { modules, isEntitlementsLoading } = get();
        if (isEntitlementsLoading) return false;
        return modules.some((m) => m.module_key === moduleKey && m.entitled);
      },

      hasAnyModule: () => {
        const { modules, isEntitlementsLoading } = get();
        if (isEntitlementsLoading) return false;
        return modules.some((m) => m.entitled);
      },

      getEntitledModules: () => {
        return get().modules.filter((m) => m.entitled);
      },
    }),
    { name: "entitlements-store" },
  ),
);

export const useCompanyModules = () =>
  useEntitlementsStore((state) => state.modules);
export const useEntitlementsLoading = () =>
  useEntitlementsStore((state) => state.isEntitlementsLoading);
export const useHasModule = (moduleKey: ModuleKey) =>
  useEntitlementsStore((state) => state.hasModule(moduleKey));
export const useHasAnyModule = () =>
  useEntitlementsStore((state) => state.hasAnyModule());
export const useEntitledModules = () => {
  const modules = useEntitlementsStore((state) => state.modules);
  return useMemo(() => modules.filter((m) => m.entitled), [modules]);
};
export const useFetchEntitlements = () =>
  useEntitlementsStore((state) => state.fetchEntitlements);

export const BILLING_MPA_URL =
  (import.meta.env.VITE_BILLING_MPA_URL as string | undefined) || "";
